// İSG Portal — dil seçici (Radix Select). Aktif diller backend'den (`/languages`)
// gelir; seçim `changeCulture` ile uygulanır + kalıcılaştırılır. Ortak component:
// tüm portallar (header) aynı seçiciyi kullanır.

import * as Select from '@radix-ui/react-select';
import { Check, ChevronDown, Globe } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { changeCulture, getAvailableLanguages } from '../i18n/i18n';
import { cn } from './cn';

export function LanguageSwitcher({ className }: { className?: string }) {
  const { i18n } = useTranslation();
  const [busy, setBusy] = useState(false);
  const languages = getAvailableLanguages();

  async function onValueChange(culture: string) {
    if (culture === i18n.language) return;
    setBusy(true);
    try {
      await changeCulture(culture);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Select.Root value={i18n.language} onValueChange={onValueChange} disabled={busy}>
      <Select.Trigger
        aria-label="Dil / Language / Sprache"
        className={cn(
          'inline-flex h-9 items-center gap-2 rounded-md border border-border-strong bg-surface px-3 text-sm text-fg',
          'hover:bg-surface-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          'disabled:opacity-60',
          className,
        )}
      >
        <Globe className="h-4 w-4 text-muted" aria-hidden="true" />
        <Select.Value />
        <Select.Icon>
          <ChevronDown className="h-4 w-4 text-muted" aria-hidden="true" />
        </Select.Icon>
      </Select.Trigger>
      <Select.Portal>
        <Select.Content
          position="popper"
          sideOffset={4}
          className="z-[var(--z-dropdown)] overflow-hidden rounded-md border border-border bg-surface shadow-md"
        >
          <Select.Viewport className="p-1">
            {languages.map((lang) => (
              <Select.Item
                key={lang.culture}
                value={lang.culture}
                className={cn(
                  'relative flex cursor-pointer select-none items-center gap-2 rounded px-2 py-1.5 pr-8 text-sm text-fg',
                  'outline-none data-[highlighted]:bg-surface-muted',
                )}
              >
                <Select.ItemText>{lang.name}</Select.ItemText>
                <Select.ItemIndicator className="absolute right-2 inline-flex">
                  <Check className="h-4 w-4 text-primary" aria-hidden="true" />
                </Select.ItemIndicator>
              </Select.Item>
            ))}
          </Select.Viewport>
        </Select.Content>
      </Select.Portal>
    </Select.Root>
  );
}
